import { useEffect, useMemo, useState } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Cell, PieChart, Pie, Legend, LabelList,
} from 'recharts'
import {
  TrendingUp, TrendingDown, Wallet, ShoppingCart, ArrowDownCircle, ArrowUpCircle,
  Package, Truck, Users, CreditCard, Trophy, RefreshCw, Info, BarChart3,
  CalendarRange, AlertTriangle, Snowflake, Briefcase, UserPlus, UserMinus,
} from 'lucide-react'

// Página PÚBLICA (sem login) — Painel do Dono / VELORE ERP.
const BASE = (import.meta as any).env?.VITE_API_URL || '/api'

const PALETA = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899',
  '#06b6d4', '#84cc16', '#a855f7', '#f97316', '#14b8a6', '#eab308']

const brl = (v: number) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const brlc = (v: number) => {
  const n = v || 0
  if (Math.abs(n) >= 1_000_000) return `R$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `R$${(n / 1_000).toFixed(0)}k`
  return `R$${n.toFixed(0)}`
}
const num = (v: number) => (v || 0).toLocaleString('pt-BR')
const pct = (v: number | null) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`)

async function getJSON(path: string) {
  const r = await fetch(`${BASE}${path}`)
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export default function PainelDono() {
  const hoje = new Date()
  const [aba, setAba] = useState<'visao' | 'dre'>('visao')
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [data, setData] = useState<any>(null)
  const [dre, setDre] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [explica, setExplica] = useState(false)

  const carregar = () => {
    setLoading(true); setErro(null)
    getJSON(`/painel-dono/resumo?ano=${ano}&mes=${mes}`)
      .then(setData).catch(() => setErro('Não foi possível carregar os dados.'))
      .finally(() => setLoading(false))
  }
  useEffect(carregar, [ano, mes])
  useEffect(() => {
    if (aba === 'dre' && (!dre || dre.ano !== ano))
      getJSON(`/painel-dono/dre?ano=${ano}`).then(setDre).catch(() => {})
  }, [aba, ano])

  // Atalhos de período
  const isMesAtual = ano === hoje.getFullYear() && mes === hoje.getMonth() + 1
  const mesAntDate = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
  const isMesAnterior = ano === mesAntDate.getFullYear() && mes === mesAntDate.getMonth() + 1
  const setMesAtual = () => { setAno(hoje.getFullYear()); setMes(hoje.getMonth() + 1) }
  const setMesAnterior = () => { setAno(mesAntDate.getFullYear()); setMes(mesAntDate.getMonth() + 1) }
  const navMes = (delta: number) => {
    const d = new Date(ano, mes - 1 + delta, 1)
    setAno(d.getFullYear()); setMes(d.getMonth() + 1)
  }

  const k = data?.kpis
  const [drill, setDrill] = useState<{ tipo: string; id?: number; titulo?: string } | null>(null)
  const abrir = (tipo: string, id?: number, titulo?: string) => setDrill({ tipo, id, titulo })

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 text-slate-800">
      {/* HEADER */}
      <header className="sticky top-0 z-20 bg-gradient-to-r from-indigo-700 via-indigo-600 to-sky-600 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-indigo-200">Painel do Dono</p>
            <h1 className="text-base sm:text-xl font-bold truncate">{data?.empresa || 'VELORE ERP'}</h1>
          </div>
          <button onClick={carregar}
            className="shrink-0 flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2 text-xs font-medium transition">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
        {/* Abas */}
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          {([['visao', 'Visão Geral', BarChart3], ['dre', 'DRE', CalendarRange]] as const).map(([id, lbl, Ic]) => (
            <button key={id} onClick={() => setAba(id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition ${
                aba === id ? 'bg-slate-100 text-indigo-700' : 'text-indigo-100 hover:bg-white/10'}`}>
              <Ic size={15} /> {lbl}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 space-y-4">
        {/* SELETOR DE PERÍODO */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase mr-1 hidden sm:inline">Período</span>
          <button onClick={setMesAtual}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              isMesAtual ? 'bg-indigo-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            Mês atual
          </button>
          <button onClick={setMesAnterior}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              isMesAnterior ? 'bg-indigo-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            Mês anterior
          </button>
          <div className="flex items-center gap-1 ml-auto bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => navMes(-1)} className="px-2.5 py-1.5 rounded hover:bg-white text-slate-600">‹</button>
            <span className="px-2 text-sm font-bold text-slate-700 min-w-[84px] text-center">{data?.periodo?.label || `${mes}/${ano}`}</span>
            <button onClick={() => navMes(1)} className="px-2.5 py-1.5 rounded hover:bg-white text-slate-600">›</button>
          </div>
        </div>

        {erro && <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">{erro}</div>}

        {aba === 'visao' && k && (
          <>
            {/* DESTAQUE VENDAS */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Hero
                titulo="Vendas no mês" valor={brl(k.venda_mes)} icon={ShoppingCart} grad="from-emerald-500 to-teal-600"
                sub={`${num(k.venda_qtd_mes)} pedidos · ticket ${brl(k.ticket_medio)}`}
                variacao={k.venda_var_pct} compara="vs mês anterior"
                onClick={() => abrir('vendas_dia', undefined, 'Vendas dia a dia')} />
              <Hero
                titulo="Vendas hoje" valor={brl(k.venda_hoje)} icon={TrendingUp} grad="from-indigo-500 to-violet-600"
                sub={`${num(k.venda_qtd_hoje)} pedidos hoje`}
                onClick={() => abrir('pedidos_hoje', undefined, 'Pedidos de hoje')} />
              <Hero
                titulo="Saldo em caixa" valor={brl(k.saldo_caixa)} icon={Wallet} grad="from-sky-500 to-blue-600"
                sub="Soma das contas bancárias"
                onClick={() => abrir('bancos', undefined, 'Saldo por banco')} />
              <Hero
                titulo="Cargas em aberto" valor={brl(k.cargas_abertas_valor)} icon={Truck} grad="from-amber-500 to-orange-600"
                sub={`${k.cargas_abertas_qtd} cargas rodando`}
                onClick={() => abrir('cargas_lista', undefined, 'Cargas em aberto')} />
            </section>

            {/* FINANCEIRO — RECEBER / PAGAR (clicável → drill-down) */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <FinCard
                tipo="receber" titulo="Contas a Receber" cor="emerald" icon={ArrowDownCircle}
                aberto={k.a_receber} recebido={k.recebido_mes} recebidoLabel="Recebido no mês"
                vencido={k.a_receber_vencido} vencidoQtd={k.a_receber_vencido_qtd} />
              <FinCard
                tipo="pagar" titulo="Contas a Pagar" cor="rose" icon={ArrowUpCircle}
                aberto={k.a_pagar} recebido={k.pago_mes} recebidoLabel="Pago no mês"
                vencido={k.a_pagar_vencido} vencidoQtd={k.a_pagar_vencido_qtd} />
            </section>

            {/* EVOLUÇÃO 12 MESES */}
            <Card titulo="Evolução das vendas (12 meses)" icon={BarChart3}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.evolucao} margin={{ top: 16, right: 4, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={44} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={brlc} width={48} />
                  <Tooltip formatter={(v: any) => brl(v)} labelStyle={{ fontWeight: 700 }} />
                  <Bar dataKey="valor" name="Vendas" radius={[4, 4, 0, 0]}>
                    {data.evolucao.map((e: any, i: number) => (
                      <Cell key={i} fill={e.ano === ano && e.mes === mes ? '#4f46e5' : '#93c5fd'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* VENDEDORES + FORMAS */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Card titulo="Vendas por vendedor" icon={Users}>
                {data.vendedores.length === 0 ? <Vazio /> : (
                  <div className="space-y-2.5">
                    {data.vendedores.slice(0, 10).map((v: any, i: number) => (
                      <VendedorRow key={i} v={v} cor={PALETA[i % PALETA.length]} ano={ano} mes={mes} />
                    ))}
                  </div>
                )}
              </Card>

              <Card titulo="Formas de pagamento" icon={CreditCard}>
                {data.formas.length === 0 ? <Vazio /> : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={data.formas} dataKey="valor" nameKey="forma" cx="50%" cy="50%"
                        innerRadius={48} outerRadius={88} paddingAngle={2}>
                        {data.formas.map((_: any, i: number) => <Cell key={i} fill={PALETA[i % PALETA.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v: any) => brl(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </section>

            {/* ESTOQUE REVENDA */}
            <Card titulo="Posição de estoque — revenda" icon={Package}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <MiniKpi label="Valor a custo" valor={brl(k.estoque_custo)} cor="text-slate-700"
                  sub={`${num(k.estoque_qtd)} produtos`} />
                <MiniKpi label="Valor a preço de venda" valor={brl(k.estoque_venda)} cor="text-emerald-600"
                  sub="Quanto vale se vender tudo" />
                <MiniKpi label="Margem potencial" valor={`${k.estoque_margem_pct.toFixed(1)}%`} cor="text-indigo-600"
                  sub={`Lucro bruto ${brl(k.estoque_venda - k.estoque_custo)}`} />
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart layout="vertical" data={[
                  { nome: 'Custo', valor: k.estoque_custo, fill: '#64748b' },
                  { nome: 'Venda', valor: k.estoque_venda, fill: '#10b981' },
                ]} margin={{ left: 8, right: 60 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="nome" tick={{ fontSize: 12 }} width={56} />
                  <Tooltip formatter={(v: any) => brl(v)} />
                  <Bar dataKey="valor" radius={[0, 6, 6, 0]}>
                    {[0, 1].map((i) => <Cell key={i} fill={i === 0 ? '#64748b' : '#10b981'} />)}
                    <LabelList dataKey="valor" position="right" formatter={brlc} style={{ fontSize: 11, fontWeight: 700 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <button onClick={() => abrir('estoque_produtos', undefined, 'Estoque de revenda por produto')}
                className="mt-2 w-full text-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg py-2 transition">
                Ver produtos por valor de estoque ▸
              </button>
              <p className="text-[11px] text-slate-400 mt-1">
                Considera apenas produtos de <b>revenda</b> com estoque positivo. Custo = estoque × custo médio. Venda = estoque × preço de venda.
              </p>
            </Card>

            {/* COMODATO + RH */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <ComodatoCard co={data.comodato} onOpen={() => abrir('comodato_freezers', undefined, 'Freezers em comodato')} />
              <RhCard rh={data.rh} />
            </section>

            {/* CARGAS EM ABERTO */}
            <Card titulo={`Cargas em aberto — ${brl(k.cargas_abertas_valor)}`} icon={Truck}>
              {data.cargas.length === 0 ? <Vazio /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.cargas.map((c: any) => (
                    <button key={c.numero} onClick={() => abrir('carga_pedidos', c.id, `Carga #${c.numero} · ${c.motorista}`)}
                      className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5 bg-slate-50 hover:bg-white hover:border-indigo-300 transition text-left">
                      <div className="w-11 h-11 shrink-0 rounded-lg bg-indigo-600 text-white grid place-items-center font-bold text-sm">
                        #{c.numero}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.motorista}</p>
                        <p className="text-[11px] text-slate-500">{c.placa} · {c.qtd_pedidos} pedidos · {c.data.split('-').reverse().join('/')}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-slate-900">{brl(c.valor)}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusCor(c.status)}`}>{statusLbl(c.status)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* TOP CLIENTES */}
            <Card titulo="Top 10 clientes do mês" icon={Trophy}>
              {data.top_clientes.length === 0 ? <Vazio /> : (
                <div className="divide-y divide-slate-100">
                  {data.top_clientes.map((c: any, i: number) => (
                    <button key={i} onClick={() => abrir('cliente_pedidos', c.cliente_id, c.nome)}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition">
                      <span className={`w-6 h-6 shrink-0 grid place-items-center rounded-full text-xs font-bold ${
                        i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                      <span className="flex-1 text-sm font-medium text-slate-700 truncate">{c.nome}</span>
                      <span className="text-xs text-slate-400">{num(c.qtd)}x</span>
                      <span className="text-sm font-bold text-slate-900 w-24 text-right">{brl(c.valor)}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* EXPLICAÇÕES */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <button onClick={() => setExplica((s) => !s)}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-slate-600">
                <Info size={16} className="text-indigo-500" /> Como calculamos cada indicador
                <span className="ml-auto text-slate-400">{explica ? '−' : '+'}</span>
              </button>
              {explica && (
                <div className="px-4 pb-4 space-y-2 text-[13px] text-slate-600">
                  <Exp t="Vendas no mês">Soma do total dos pedidos faturados/expedidos/entregues no período (exclui cancelados e bonificações). O ticket é o valor dividido pela quantidade de pedidos.</Exp>
                  <Exp t="Variação %">Compara as vendas do mês selecionado com o mês imediatamente anterior.</Exp>
                  <Exp t="Contas a Receber / Pagar">Saldo em aberto = valor − valor já pago, dos títulos com status aberto/parcial. Vencido = títulos com vencimento já passado.</Exp>
                  <Exp t="Recebido / Pago no mês">Soma dos títulos baixados (com data de pagamento) dentro do período.</Exp>
                  <Exp t="Estoque de revenda">Apenas produtos de finalidade revenda com saldo positivo. A custo usa o custo médio; a venda usa o preço de tabela. Margem = (venda − custo) / venda.</Exp>
                  <Exp t="Cargas em aberto">Cargas de distribuição que ainda não foram fechadas nem canceladas. O valor soma os pedidos vinculados a cada carga.</Exp>
                </div>
              )}
            </div>
          </>
        )}

        {aba === 'dre' && <DreTab dre={dre} ano={ano} setAno={setAno} />}

        {loading && !data && <div className="py-20 text-center text-slate-400">Carregando…</div>}
        <p className="text-center text-[11px] text-slate-400 pt-2 pb-6">
          Atualizado em {data?.atualizado_em ? new Date(data.atualizado_em).toLocaleString('pt-BR') : '—'}
        </p>
      </main>

      <DrillSheet drill={drill} ano={ano} mes={mes} onClose={() => setDrill(null)} />
    </div>
  )
}

/* ---------- Drill universal (bottom-sheet) ---------- */
function DrillSheet({ drill, ano, mes, onClose }: any) {
  const [d, setD] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!drill) { setD(null); return }
    setLoading(true); setD(null)
    const q = new URLSearchParams({ tipo: drill.tipo, ano, mes } as any)
    if (drill.id != null) q.set('id', String(drill.id))
    getJSON(`/painel-dono/drill?${q.toString()}`).then(setD).finally(() => setLoading(false))
  }, [drill])
  if (!drill) return null
  const max = d?.itens?.reduce((m: number, x: any) => Math.max(m, x.valor || 0), 0) || 1
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col animate-[slideup_.2s_ease]">
        <div className="shrink-0 px-4 pt-3 pb-2 border-b border-slate-100">
          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-2 sm:hidden" />
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800 truncate">{drill.titulo || d?.titulo || 'Detalhe'}</h3>
              {d && <p className="text-xs text-slate-500">{num(d.qtd)} itens · total {brl(d.total)}</p>}
            </div>
            <button onClick={onClose} className="shrink-0 w-8 h-8 grid place-items-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-lg">×</button>
          </div>
        </div>
        <div className="overflow-y-auto px-4 py-2">
          {loading && <p className="text-center text-slate-400 py-10">Carregando…</p>}
          {d && d.itens.length === 0 && <p className="text-center text-slate-400 py-10">Sem itens.</p>}
          {d && d.itens.map((it: any, i: number) => (
            <div key={i} className="py-2 border-b border-slate-50 last:border-0">
              <div className="flex justify-between items-baseline gap-2">
                <span className="text-sm font-medium text-slate-700 truncate">{it.label}</span>
                <span className="text-sm font-bold text-slate-900 shrink-0">{brl(it.valor)}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden my-1">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${((it.valor || 0) / max) * 100}%` }} />
              </div>
              <div className="flex justify-between items-center text-[11px] text-slate-400">
                <span className="truncate">{it.sub}</span>
                {it.tag && <span className={`shrink-0 px-1.5 py-0.5 rounded font-medium ${tagCor(it.tag)}`}>{tagLbl(it.tag)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function tagLbl(t: string) {
  return ({ faturado: 'Faturado', expedido: 'Expedido', entregue: 'Entregue', em_uso: 'Com cliente',
    livre: 'Disponível', manutencao: 'Manutenção', ...({} as any) } as any)[t] || statusLbl(t)
}
function tagCor(t: string) {
  return ({
    faturado: 'bg-blue-100 text-blue-700', expedido: 'bg-amber-100 text-amber-700',
    entregue: 'bg-emerald-100 text-emerald-700', em_uso: 'bg-cyan-100 text-cyan-700',
    livre: 'bg-slate-200 text-slate-600', manutencao: 'bg-rose-100 text-rose-700',
  } as any)[t] || statusCor(t)
}

/* ---------- componentes ---------- */

function Hero({ titulo, valor, sub, icon: Icon, grad, variacao, compara, onClick }: any) {
  const up = variacao != null && variacao >= 0
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${grad} text-white p-4 shadow-md text-left w-full ${onClick ? 'hover:brightness-110 active:scale-[.98] transition cursor-pointer' : ''}`}>
      <Icon size={56} className="absolute -right-2 -bottom-2 opacity-15" />
      {onClick && <span className="absolute top-2 right-2 text-[10px] text-white/70">ver ▸</span>}
      <p className="text-[11px] font-medium text-white/80 uppercase tracking-wide">{titulo}</p>
      <p className="text-lg sm:text-2xl font-extrabold mt-1 leading-tight break-words">{valor}</p>
      {sub && <p className="text-[11px] text-white/80 mt-1 leading-snug">{sub}</p>}
      {variacao != null && (
        <div className="mt-2 inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5 text-[11px] font-bold">
          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {pct(variacao)}
          <span className="font-normal text-white/70">{compara}</span>
        </div>
      )}
    </button>
  )
}

function FinCard({ tipo, titulo, icon: Icon, cor, aberto, recebido, recebidoLabel, vencido, vencidoQtd }: any) {
  const cores: any = {
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-700',
    rose: 'from-rose-50 to-white border-rose-200 text-rose-700',
  }
  const barra: any = { emerald: 'bg-emerald-500', rose: 'bg-rose-500' }
  const [open, setOpen] = useState(false)
  const [det, setDet] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const abrir = () => {
    const novo = !open
    setOpen(novo)
    if (novo && !det) {
      setLoading(true)
      getJSON(`/painel-dono/detalhe-aberto?tipo=${tipo}`).then(setDet).finally(() => setLoading(false))
    }
  }
  const max = det?.grupos?.[0]?.valor || 1
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${cores[cor]} p-4 shadow-sm`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} /> <h3 className="font-bold text-sm uppercase tracking-wide">{titulo}</h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={abrir}
          className="text-left rounded-lg -m-1 p-1 hover:bg-white/60 transition group">
          <p className="text-[11px] text-slate-500 flex items-center gap-1">
            Em aberto <span className="text-slate-400 group-hover:text-indigo-500">{open ? '▾' : '▸ ver detalhe'}</span>
          </p>
          <p className="text-xl font-extrabold text-slate-800">{brl(aberto)}</p>
        </button>
        <div>
          <p className="text-[11px] text-slate-500">{recebidoLabel}</p>
          <p className="text-xl font-extrabold text-slate-800">{brl(recebido)}</p>
        </div>
      </div>
      {vencido > 0 && (
        <div className="mt-3 flex items-center gap-1.5 bg-white/70 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-amber-700">
          <AlertTriangle size={14} /> {num(vencidoQtd)} vencidos · {brl(vencido)}
        </div>
      )}
      {open && (
        <div className="mt-3 border-t border-white/60 pt-3">
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-2">
            {det?.chave_label || (tipo === 'receber' ? 'Por forma de cobrança' : 'Por fornecedor')}
          </p>
          {loading && <p className="text-xs text-slate-400 py-3 text-center">Carregando…</p>}
          {det && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {det.grupos.map((g: any, i: number) => (
                <div key={i}>
                  <div className="flex justify-between items-baseline text-[13px]">
                    <span className="font-medium text-slate-700 truncate pr-2">{rotuloCobranca(g.chave)}</span>
                    <span className="font-bold text-slate-900 shrink-0">{brl(g.valor)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                    <div className={`h-full rounded-full ${barra[cor]}`} style={{ width: `${(g.valor / max) * 100}%` }} />
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                    <span>{num(g.qtd)} títulos</span>
                    {g.vencido > 0 && <span className="text-amber-600 font-medium">{num(g.vencido_qtd)} vencidos · {brl(g.vencido)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const COBRANCA_LBL: any = {
  boleto: 'Boleto', dinheiro: 'Dinheiro', prazo: 'A prazo (carteira)', pix: 'PIX',
  pix_pendente: 'PIX pendente', cheque: 'Cheque', cheque_devolvido: 'Cheque devolvido',
  cartao_credito: 'Cartão de crédito', cartao_debito: 'Cartão de débito',
  'não informado': 'Não informado',
}
function rotuloCobranca(c: string) {
  return COBRANCA_LBL[c] || c
}

function VendedorRow({ v, cor, ano, mes }: any) {
  const [open, setOpen] = useState(false)
  const [cli, setCli] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const abrir = () => {
    const novo = !open
    setOpen(novo)
    if (novo && !cli) {
      setLoading(true)
      const q = v.vendedor_id ? `vendedor_id=${v.vendedor_id}&` : ''
      getJSON(`/painel-dono/vendedor-clientes?${q}ano=${ano}&mes=${mes}`).then(setCli).finally(() => setLoading(false))
    }
  }
  const max = cli?.grupos?.[0]?.valor || 1
  return (
    <div>
      <button onClick={abrir} className="w-full text-left">
        <div className="flex justify-between items-baseline text-sm mb-1">
          <span className="font-medium text-slate-700 truncate pr-2">
            <span className="text-slate-400 mr-1">{open ? '▾' : '▸'}</span>{v.nome}
          </span>
          <span className="font-bold text-slate-900 shrink-0">{brl(v.valor)}</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${v.pct}%`, background: cor }} />
        </div>
        <div className="flex justify-between text-[11px] text-slate-400 mt-0.5">
          <span>{v.pct}% do total · {num(v.qtd)} pedidos</span>
          <span>ticket {brl(v.ticket)}</span>
        </div>
      </button>
      {open && (
        <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-100 space-y-1.5">
          {loading && <p className="text-xs text-slate-400 py-1">Carregando clientes…</p>}
          {cli && cli.grupos.length === 0 && <p className="text-xs text-slate-400 py-1">Sem clientes no período.</p>}
          {cli && cli.grupos.slice(0, 15).map((c: any, i: number) => (
            <div key={i}>
              <div className="flex justify-between items-baseline text-[12px]">
                <span className="text-slate-600 truncate pr-2">{c.cliente}</span>
                <span className="font-semibold text-slate-800 shrink-0">{brl(c.valor)}</span>
              </div>
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                <div className="h-full rounded-full" style={{ width: `${(c.valor / max) * 100}%`, background: cor }} />
              </div>
              <p className="text-[10px] text-slate-400">{num(c.qtd)}x · última {c.ultima?.split('-').reverse().join('/')}</p>
            </div>
          ))}
          {cli && cli.grupos.length > 15 && (
            <p className="text-[10px] text-slate-400 pt-1">+{cli.grupos.length - 15} clientes…</p>
          )}
        </div>
      )}
    </div>
  )
}

function ComodatoCard({ co, onOpen }: any) {
  if (!co) return null
  const oc = co.ocupacao_pct
  return (
    <div className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-4 shadow-sm">
      <button onClick={onOpen} className="flex items-center gap-2 text-sm font-bold text-cyan-800 mb-3 hover:text-cyan-600 transition">
        <Snowflake size={16} /> Comodato — Freezers <span className="text-[11px] font-normal text-cyan-500">ver lista ▸</span>
      </button>
      <div className="flex items-center gap-4">
        {/* Gauge de ocupação */}
        <div className="relative w-24 h-24 shrink-0">
          <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e0f2fe" strokeWidth="3.5" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#0891b2" strokeWidth="3.5"
              strokeDasharray={`${oc} ${100 - oc}`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <p className="text-xl font-extrabold text-cyan-700 leading-none">{oc}%</p>
              <p className="text-[9px] text-slate-400 uppercase">ocupação</p>
            </div>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-lg bg-cyan-100/60 p-2">
            <p className="text-lg font-extrabold text-cyan-800">{co.com_clientes}</p>
            <p className="text-[10px] text-slate-500 uppercase">com clientes</p>
          </div>
          <div className="rounded-lg bg-slate-100 p-2">
            <p className="text-lg font-extrabold text-slate-700">{co.disponiveis}</p>
            <p className="text-[10px] text-slate-500 uppercase">disponíveis</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2">
            <p className="text-sm font-bold text-slate-700">{co.clientes_atendidos}</p>
            <p className="text-[10px] text-slate-500 uppercase">clientes</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2">
            <p className="text-sm font-bold text-slate-700">{co.total_freezers}</p>
            <p className="text-[10px] text-slate-500 uppercase">total parque</p>
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-between text-[12px] border-t border-cyan-100 pt-2">
        <span className="text-slate-500">Patrimônio em campo: <b className="text-slate-700">{brl(co.valor_em_campo)}</b></span>
        <span className="text-slate-500">Total: <b className="text-slate-700">{brl(co.valor_total)}</b></span>
      </div>
    </div>
  )
}

function RhCard({ rh }: any) {
  if (!rh) return null
  const vazio = rh.ativos === 0
  return (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
      <h3 className="flex items-center gap-2 text-sm font-bold text-violet-800 mb-3">
        <Briefcase size={16} /> RH — Equipe
      </h3>
      {vazio ? (
        <p className="text-center text-sm text-slate-400 py-8">Nenhum colaborador cadastrado ainda.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-violet-100/60 p-2.5">
              <p className="text-2xl font-extrabold text-violet-800">{rh.ativos}</p>
              <p className="text-[10px] text-slate-500 uppercase">colaboradores ativos</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-2.5">
              <p className="text-2xl font-extrabold text-slate-700">{brl(rh.folha_mensal)}</p>
              <p className="text-[10px] text-slate-500 uppercase">folha mensal</p>
            </div>
          </div>
          <div className="flex gap-2 mb-3 text-[12px]">
            <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 rounded px-2 py-1">
              <UserPlus size={13} /> {rh.admissoes_mes} admissões</span>
            <span className="flex items-center gap-1 text-rose-700 bg-rose-50 rounded px-2 py-1">
              <UserMinus size={13} /> {rh.demissoes_mes} desligamentos</span>
            <span className="ml-auto text-slate-500 self-center">custo médio {brl(rh.custo_medio)}</span>
          </div>
          {rh.departamentos.length > 0 && (
            <div className="space-y-1.5 border-t border-violet-100 pt-2">
              {rh.departamentos.slice(0, 5).map((d: any, i: number) => {
                const max = rh.departamentos[0].qtd || 1
                return (
                  <div key={i} className="flex items-center gap-2 text-[12px]">
                    <span className="w-28 truncate text-slate-600">{d.nome}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-violet-500" style={{ width: `${(d.qtd / max) * 100}%` }} />
                    </div>
                    <span className="font-bold text-slate-700 w-6 text-right">{d.qtd}</span>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Card({ titulo, icon: Icon, children }: any) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <h3 className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
        {Icon && <Icon size={16} className="text-indigo-500" />} {titulo}
      </h3>
      {children}
    </section>
  )
}

function MiniKpi({ label, valor, sub, cor }: any) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
      <p className="text-[11px] text-slate-500 uppercase font-medium">{label}</p>
      <p className={`text-xl font-extrabold mt-0.5 ${cor}`}>{valor}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  )
}

function Exp({ t, children }: any) {
  return <p><b className="text-slate-700">{t}:</b> {children}</p>
}
function Vazio() {
  return <p className="text-center text-sm text-slate-400 py-8">Sem dados no período.</p>
}
function statusLbl(s: string) {
  return ({ aberta: 'Aberta', faturada: 'Faturada', em_rota: 'Em rota', em_acerto: 'Em acerto' } as any)[s] || s
}
function statusCor(s: string) {
  return ({
    aberta: 'bg-slate-200 text-slate-600', faturada: 'bg-blue-100 text-blue-700',
    em_rota: 'bg-amber-100 text-amber-700', em_acerto: 'bg-violet-100 text-violet-700',
  } as any)[s] || 'bg-slate-100 text-slate-600'
}

/* ---------- DRE ---------- */
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function DreTab({ dre, ano, setAno }: any) {
  if (!dre) return <div className="py-20 text-center text-slate-400">Carregando DRE…</div>
  const t = dre.totalizadores
  return (
    <Card titulo="DRE — Demonstrativo de Resultado" icon={CalendarRange}>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setAno(ano - 1)} className="px-2.5 py-1 rounded bg-slate-100 hover:bg-slate-200">‹</button>
        <span className="font-bold text-slate-700">{ano}</span>
        <button onClick={() => setAno(ano + 1)} className="px-2.5 py-1 rounded bg-slate-100 hover:bg-slate-200">›</button>
      </div>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-[12px] border-collapse min-w-[760px]">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left font-semibold py-1.5 sticky left-0 bg-white">Conta gerencial</th>
              {MESES.map((m) => <th key={m} className="text-right font-semibold px-1.5">{m}</th>)}
              <th className="text-right font-bold px-2 bg-slate-50">Total</th>
            </tr>
          </thead>
          <tbody>
            {dre.grupos.map((g: any) => (
              <GrupoDre key={g.tipo} g={g} />
            ))}
            <LinhaTot label="= RECEITA LÍQUIDA" tt={t.receita_liquida} forte />
            <LinhaTot label="= MARGEM DE CONTRIBUIÇÃO" tt={t.margem_contribuicao} pct={t.margem_contribuicao.pct_total} />
            <LinhaTot label="= LUCRO LÍQUIDO" tt={t.lucro_liquido} pct={t.lucro_liquido.pct_total} forte verde={t.lucro_liquido.total >= 0} />
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Construído dinamicamente a partir dos lançamentos por <b>conta gerencial</b>. Role na horizontal para ver todos os meses.
      </p>
    </Card>
  )
}

function GrupoDre({ g }: any) {
  const [open, setOpen] = useState(false)
  const corHead = g.cor === 'verde' ? 'text-emerald-700' : 'text-rose-700'
  return (
    <>
      <tr className="border-t border-slate-200 cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)}>
        <td className={`py-1.5 font-bold sticky left-0 bg-white ${corHead}`}>{open ? '▾' : '▸'} {g.label}</td>
        {g.totais_mes.map((v: number, i: number) => (
          <td key={i} className="text-right px-1.5 tabular-nums text-slate-600">{v ? brlMini(v) : '·'}</td>
        ))}
        <td className={`text-right px-2 font-bold tabular-nums bg-slate-50 ${corHead}`}>{brlMini(g.total)}</td>
      </tr>
      {open && g.items.map((it: any) => (
        <tr key={it.codigo} className="text-slate-500">
          <td className="py-1 pl-5 sticky left-0 bg-white whitespace-nowrap">{it.codigo} {it.nome}</td>
          {it.meses.map((v: number, i: number) => (
            <td key={i} className="text-right px-1.5 tabular-nums">{v ? brlMini(v) : '·'}</td>
          ))}
          <td className="text-right px-2 font-semibold tabular-nums bg-slate-50">{brlMini(it.total)}</td>
        </tr>
      ))}
    </>
  )
}

function LinhaTot({ label, tt, pct: p, forte, verde }: any) {
  return (
    <tr className={`border-t-2 ${forte ? 'border-slate-300' : 'border-slate-100'}`}>
      <td className={`py-1.5 sticky left-0 bg-white font-bold ${verde === false ? 'text-rose-700' : verde ? 'text-emerald-700' : 'text-slate-700'}`}>
        {label}{p != null && <span className="text-[10px] font-normal text-slate-400"> ({p.toFixed(1)}%)</span>}
      </td>
      {tt.meses.map((v: number, i: number) => (
        <td key={i} className="text-right px-1.5 tabular-nums font-medium text-slate-600">{v ? brlMini(v) : '·'}</td>
      ))}
      <td className="text-right px-2 font-extrabold tabular-nums bg-slate-100">{brlMini(tt.total)}</td>
    </tr>
  )
}

const brlMini = (v: number) =>
  (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
